import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Trophy, Heart, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GameSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadUser();
    loadSession();
    // Poll for updates every 5 seconds
    const interval = setInterval(loadSession, 5000);
    return () => clearInterval(interval);
  }, [sessionId]);

  const loadUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(res.data);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadSession = async () => {
    try {
      const res = await axios.get(`${API}/games/session/${sessionId}`, { withCredentials: true });
      setSession(res.data.session);
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async () => {
    try {
      await axios.post(`${API}/games/session/${sessionId}/accept`, {}, { withCredentials: true });
      toast.success('Game started!');
      loadSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    }
  };

  const submitAnswer = async () => {
    if (!answer.trim() && session.current_question?.question_type !== 'choice') {
      toast.error('Please enter an answer');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/games/session/${sessionId}/answer`,
        {
          question_id: session.current_question.id,
          answer: answer
        },
        { withCredentials: true }
      );
      
      toast.success('Answer submitted!');
      setAnswer('');
      loadSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const selectChoice = async (choice) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/games/session/${sessionId}/answer`,
        {
          question_id: session.current_question.id,
          answer: choice
        },
        { withCredentials: true }
      );
      
      toast.success('Answer submitted!');
      loadSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const isMyTurn = () => {
    if (!session || !currentUser) return false;
    if (session.mode === 'real_time' || session.mode === 'hybrid') return true;
    return session.current_turn === currentUser.id;
  };

  const hasAnsweredCurrent = () => {
    if (!session || !currentUser || !session.current_question) return false;
    return session.answers?.some(
      a => a.question_id === session.current_question.id && a.user_id === currentUser.id
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading game...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Game not found</p>
          <Button onClick={() => navigate('/games')} className="mt-4">
            Back to Games
          </Button>
        </div>
      </div>
    );
  }

  const opponent = currentUser?.id === session.player1_id ? session.player2 : session.player1;
  const progress = session.questions ? ((session.current_question_index / session.questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-3 mb-3">
            <Button
              onClick={() => navigate('/games')}
              variant="ghost"
              size="icon"
              className="hover:bg-pink-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-gray-800">
                {session.game_type === 'get_to_know' && '👋 Get To Know You'}
                {session.game_type === 'love_quiz' && '❤️ Love & Psychology Quiz'}
                {session.game_type === 'compatibility' && '🎯 Compatibility Challenge'}
              </h1>
              <p className="text-sm text-gray-600">with {opponent?.name}</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-pink-500 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right">
            Question {session.current_question_index + 1} of {session.questions?.length || 0}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Pending Invitation */}
        {session.status === 'pending' && currentUser?.id === session.player2_id && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center">
            <div className="text-6xl mb-4">🎮</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Game Invitation!</h2>
            <p className="text-gray-600 mb-6">
              {session.player1?.name} invited you to play
            </p>
            <Button
              onClick={acceptInvitation}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
            >
              Accept & Start Game
            </Button>
          </div>
        )}

        {/* Waiting for Opponent to Accept */}
        {session.status === 'pending' && currentUser?.id === session.player1_id && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center">
            <div className="animate-pulse text-6xl mb-4">⏳</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Waiting for {opponent?.name}</h2>
            <p className="text-gray-600">
              They'll receive a notification about your invitation
            </p>
          </div>
        )}

        {/* Active Game */}
        {session.status === 'active' && session.current_question && (
          <div className="space-y-4">
            {/* Players */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
              <div className="flex items-center justify-around">
                <div className="text-center">
                  <Avatar className="w-16 h-16 mx-auto mb-2">
                    <AvatarImage src={session.player1?.photos?.[0] || session.player1?.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white">
                      {session.player1?.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <p className="text-sm font-semibold text-gray-800">{session.player1?.name}</p>
                  {session.mode === 'turn_based' && session.current_turn === session.player1_id && (
                    <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      Their turn
                    </span>
                  )}
                </div>
                
                <div className="text-2xl">💕</div>
                
                <div className="text-center">
                  <Avatar className="w-16 h-16 mx-auto mb-2">
                    <AvatarImage src={session.player2?.photos?.[0] || session.player2?.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white">
                      {session.player2?.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <p className="text-sm font-semibold text-gray-800">{session.player2?.name}</p>
                  {session.mode === 'turn_based' && session.current_turn === session.player2_id && (
                    <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      Their turn
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Current Question */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold mb-3">
                  {session.current_question.category}
                </span>
                <h2 className="text-2xl font-bold text-gray-800">
                  {session.current_question.question}
                </h2>
              </div>

              {/* Already Answered */}
              {hasAnsweredCurrent() && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center space-x-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">You've answered this question!</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    {session.mode === 'turn_based' 
                      ? 'Waiting for the next question...' 
                      : 'Waiting for your partner to answer...'}
                  </p>
                </div>
              )}

              {/* Answer Options (Multiple Choice) */}
              {session.current_question.question_type === 'choice' && 
               session.current_question.options && 
               !hasAnsweredCurrent() && 
               isMyTurn() && (
                <div className="space-y-2">
                  {session.current_question.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => selectChoice(option)}
                      disabled={submitting}
                      className="w-full p-4 text-left rounded-xl bg-gradient-to-r from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100 border-2 border-purple-200 hover:border-purple-400 transition-all disabled:opacity-50"
                    >
                      <span className="font-semibold text-gray-800">{option}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Open Answer */}
              {session.current_question.question_type === 'open' && 
               !hasAnsweredCurrent() && 
               isMyTurn() && (
                <div>
                  <Textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Type your answer here..."
                    rows={4}
                    className="mb-3"
                  />
                  <Button
                    onClick={submitAnswer}
                    disabled={submitting || !answer.trim()}
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? 'Submitting...' : 'Submit Answer'}
                  </Button>
                </div>
              )}

              {/* Not Your Turn */}
              {!isMyTurn() && !hasAnsweredCurrent() && (
                <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 text-center">
                  <p className="text-gray-600">⏳ Waiting for {opponent?.name} to answer...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Completed Game */}
        {session.status === 'completed' && session.result && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center">
            <div className="text-6xl mb-4">
              <Trophy className="w-16 h-16 mx-auto text-yellow-500" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Game Complete!</h2>
            
            {session.result.compatibility_score && (
              <div className="my-6">
                <div className="text-6xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 text-transparent bg-clip-text">
                  {session.result.compatibility_score}%
                </div>
                <p className="text-gray-600 mt-2">Compatibility Score</p>
              </div>
            )}

            <div className="space-y-2 mb-6">
              {session.result.insights?.map((insight, index) => (
                <div key={index} className="bg-purple-50 rounded-lg p-3">
                  <p className="text-gray-800">{insight}</p>
                </div>
              ))}
            </div>

            <div className="flex space-x-3">
              <Button
                onClick={() => navigate('/games')}
                variant="outline"
                className="flex-1"
              >
                Back to Games
              </Button>
              <Button
                onClick={() => navigate(`/chat/${session.player1_id === currentUser?.id ? session.player2_id : session.player1_id}`)}
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500"
              >
                <Heart className="w-4 h-4 mr-2" />
                Chat
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameSession;
